/**
 * Picks which course-tab block lists the campus accordion should render.
 * A selected tab keeps the current single-tab view; campus home lists every tab that has blocks.
 */

import { getContenidoNodesForTab, resolvePublishedCourseTabsAndContenido } from "./normalizeCourseShape";
import { temarioNodeHasVisibleContent } from "./campusVisibleContent";
import { applyCatalogTestsToTabTree } from "./filterTemarioTestsForTab";

/**
 * Drops empty blocks but keeps each remaining block's index in the original tab
 * tree. Deep links use that index; compacting the array would point them at the
 * wrong accordion row. Catalog blocks appended after that tree get the next
 * free index, so they do not reuse a preserved one.
 *
 * @param {unknown[] | null | undefined} nodes
 * @param {object[] | null | undefined} courseTests
 * @param {string} tab
 * @param {string[] | null | undefined} catalogTabs
 * @returns {object[]}
 */
function blocksKeepingSourceIndex(nodes, courseTests, tab, catalogTabs) {
  const list = Array.isArray(nodes) ? nodes : [];
  /** @type {unknown[]} */
  const visible = [];
  /** @type {number[]} */
  const sourceIndexes = [];
  list.forEach((node, sourceIndex) => {
    if (temarioNodeHasVisibleContent(node)) {
      visible.push(node);
      sourceIndexes.push(sourceIndex);
    }
  });
  const withCatalog = applyCatalogTestsToTabTree(visible, courseTests, tab, catalogTabs);
  return withCatalog.map((block, index) => ({
    ...block,
    sourceIndex: index < sourceIndexes.length
      ? sourceIndexes[index]
      : list.length + (index - sourceIndexes.length),
  }));
}

/**
 * @param {Record<string, unknown[]> | null | undefined} contenido
 * @param {string[] | null | undefined} pestanasCurso
 * @param {string} [activeTab]
 * @param {object[] | null | undefined} [courseTests]
 * @returns {Array<{ tab: string, blocks: unknown[] }>}
 */
export function listCampusAccordionSections(contenido, pestanasCurso, activeTab = "", courseTests) {
  const published = resolvePublishedCourseTabsAndContenido(pestanasCurso, contenido);
  const content = published.contenido;
  const selected = typeof activeTab === "string" ? activeTab.trim() : "";
  const catalogTabs = published.pestanasCurso;

  if (selected) {
    return [
      {
        tab: selected,
        blocks: blocksKeepingSourceIndex(
          getContenidoNodesForTab(content, selected),
          courseTests,
          selected,
          catalogTabs,
        ),
      },
    ];
  }

  return catalogTabs
    .map((tab) => ({
      tab,
      blocks: blocksKeepingSourceIndex(
        getContenidoNodesForTab(content, tab),
        courseTests,
        tab,
        catalogTabs,
      ),
    }))
    .filter((section) => section.blocks.length > 0);
}
